/**
 * meta/prize-sets.js — themed prize sets and the piece economy (GDD 8.5).
 *
 * Eight sets, 5–8 pieces each, bought one piece at a time with redemption
 * tokens. Costs escalate within a set (the completion drive) and every ramp in
 * `PRIZE_SET_PIECES` sums exactly to that set's `totalTokens`. Completing a set
 * unlocks its cosmetic for the carnival goose.
 *
 * **Seasonal sets are gated on the game's calendar quarter**, derived from
 * `save.today()` — so `__cc.time.advanceDays(92)` really does close Summer
 * Splash and open Spooky Carnival. Out-of-season sets stay visible with a
 * "returns in Qn" label rather than disappearing, because a set you can see but
 * cannot buy is the thing that makes the season matter.
 *
 * @module meta/prize-sets
 */

import {
  PRIZE_SETS, PRIZE_SET_PIECES, COSMETIC_CATALOG, EVENTS, META_EVENTS,
} from '../constants.js';
import { save, economy } from '../core/save.js';
import { bus } from '../core/events.js';
import { cosmetics as avatarCosmetics } from '../avatar/cosmetics.js';
import { setSlice, quarter, daysUntilQuarter } from './state.js';

const COSMETIC_BY_ID = Object.fromEntries(COSMETIC_CATALOG.map((c) => [c.id, c]));

/** @param {string} setId @returns {object|undefined} */
function defOf(setId) {
  return PRIZE_SETS.find((s) => s.id === setId);
}

/** @param {string} pieceId @returns {{ set: object, piece: object, index: number }|null} */
function locate(pieceId) {
  for (const set of PRIZE_SETS) {
    const list = PRIZE_SET_PIECES[set.id] || [];
    const index = list.findIndex((p) => p.id === pieceId);
    if (index >= 0) return { set, piece: list[index], index };
  }
  return null;
}

export const sets = {
  /**
   * Progress for one set. Signature preserved from the stub.
   * @param {string} setId
   * @returns {{ owned: number, total: number, complete: boolean }}
   */
  progress(setId) {
    const def = defOf(setId);
    const total = def ? def.pieces : 0;
    if (!def) return { owned: 0, total: 0, complete: false };
    const slice = setSlice(setId);
    const owned = slice.pieces.length;
    return { owned, total, complete: total > 0 && owned >= total };
  },

  /**
   * Is this set purchasable right now? Permanent sets always are; seasonal
   * sets only during their GDD 8.5 quarter.
   * @param {string} setId
   * @returns {boolean}
   */
  available(setId) {
    const def = defOf(setId);
    if (!def) return false;
    if (def.availability !== 'seasonal') return true;
    return quarter() === def.season;
  },

  /**
   * Every piece of a set with its ownership state.
   * @param {string} setId
   * @returns {Array<{ id, name, cost, owned: boolean, index: number }>}
   */
  pieces(setId) {
    const slice = setSlice(setId);
    return (PRIZE_SET_PIECES[setId] || []).map((p, index) => ({
      ...p,
      index,
      owned: slice.pieces.includes(p.id),
    }));
  },

  /**
   * The next piece a player would buy — the cheapest unowned one.
   * @param {string} setId
   * @returns {object|null}
   */
  nextPiece(setId) {
    return this.pieces(setId).find((p) => !p.owned) || null;
  },

  /**
   * Full view of every set, ready for a UI to render.
   * @returns {Array<object>}
   */
  all() {
    return PRIZE_SETS.map((def) => {
      const prog = this.progress(def.id);
      const available = this.available(def.id);
      const cos = COSMETIC_BY_ID[def.cosmetic] || null;
      const seasonal = def.availability === 'seasonal';
      return {
        ...def,
        ...prog,
        available,
        seasonal,
        /** Days until the set's season returns; 0 when it is live. */
        returnsInDays: seasonal && !available ? daysUntilQuarter(def.season) : 0,
        lockLabel: seasonal && !available ? `Returns in ${def.season}` : '',
        cosmeticId: def.cosmetic,
        cosmeticName: cos ? cos.name : def.cosmetic,
        cosmeticSlot: cos ? cos.slot : null,
        cosmeticOwned: avatarCosmetics.owns(def.cosmetic),
        pieceList: this.pieces(def.id),
        spent: this.pieces(def.id).filter((p) => p.owned).reduce((a, p) => a + p.cost, 0),
      };
    });
  },

  /** @param {string} pieceId @returns {boolean} */
  owns(pieceId) {
    const found = locate(pieceId);
    if (!found) return false;
    return setSlice(found.set.id).pieces.includes(pieceId);
  },

  /**
   * Buy a piece with redemption tokens.
   *
   * Refuses (and changes nothing) when the piece is unknown, already owned, its
   * season is closed, or the player is short. Buying the last piece completes
   * the set and unlocks its cosmetic.
   *
   * @param {string} pieceId
   * @returns {{ ok: boolean, reason?: string, piece?: object, setId?: string,
   *             completed?: boolean, cosmetic?: string, spent?: number }}
   */
  purchase(pieceId) {
    const found = locate(pieceId);
    if (!found) return { ok: false, reason: 'unknown-piece' };
    const { set, piece, index } = found;

    const slice = setSlice(set.id);
    if (slice.pieces.includes(pieceId)) return { ok: false, reason: 'already-owned' };
    if (!this.available(set.id)) return { ok: false, reason: 'out-of-season' };
    if (economy.tokens() < piece.cost) return { ok: false, reason: 'insufficient-tokens' };

    if (!economy.spendTokens(piece.cost, `set:${set.id}`)) {
      return { ok: false, reason: 'insufficient-tokens' };
    }

    slice.pieces.push(pieceId);
    save.markDirty();

    // GDD 8.5: the sting climbs as the set fills, so the fifth piece sounds
    // more like an event than the first.
    const step = slice.pieces.length;
    bus.emit(EVENTS.AUDIO_PLAY, {
      name: 'set-piece',
      step,
      of: set.pieces,
      pitch: 1 + (step - 1) * 0.07,
    });
    bus.emit(META_EVENTS.PIECE_BOUGHT, {
      setId: set.id, pieceId, cost: piece.cost, step, of: set.pieces,
    });

    const completed = slice.pieces.length >= set.pieces && !slice.completed;
    let cosmetic;
    if (completed) {
      slice.completed = true;
      cosmetic = set.cosmetic;
      avatarCosmetics.unlock(cosmetic);
      save.markDirty();

      const cos = COSMETIC_BY_ID[cosmetic];
      bus.emit(EVENTS.AUDIO_PLAY, { name: 'set-complete', setId: set.id });
      bus.emit(EVENTS.UI_TOAST, {
        text: `${set.name} complete — ${cos ? cos.name : cosmetic} unlocked!`,
        kind: 'prize',
        duration: 4200,
      });
      bus.emit(META_EVENTS.SET_COMPLETE, { setId: set.id, cosmetic, name: set.name });
    } else {
      bus.emit(EVENTS.UI_TOAST, {
        text: `${piece.name} — ${step}/${set.pieces}`,
        kind: 'good',
        duration: 2000,
      });
    }

    return {
      ok: true,
      piece: { ...piece, index },
      setId: set.id,
      completed: !!completed,
      cosmetic,
      spent: piece.cost,
    };
  },

  /**
   * Can the player afford this piece right now (and is it buyable at all)?
   * @param {string} pieceId
   * @returns {boolean}
   */
  canAfford(pieceId) {
    const found = locate(pieceId);
    if (!found) return false;
    if (this.owns(pieceId)) return false;
    if (!this.available(found.set.id)) return false;
    return economy.tokens() >= found.piece.cost;
  },

  /** @returns {Array<object>} Every piece across every set — the desk catalog. */
  catalog() {
    return PRIZE_SETS.flatMap((set) =>
      this.pieces(set.id).map((p) => ({
        ...p,
        setId: set.id,
        setName: set.name,
        available: this.available(set.id),
      })),
    );
  },

  /** @returns {'Q1'|'Q2'|'Q3'|'Q4'} The quarter the game's calendar is in. */
  season() {
    return quarter();
  },
};

export default sets;
